import React, { useState, useEffect, useRef, useCallback } from 'react';
import './App.css';
import {
    Team,
    loadSettings,
    saveSettings,
    clampSettings,
    getWinner
} from './gameUtils';
import {
    CELEBRATION_COOLDOWN_MS,
    createShakeDetector,
    createCelebration,
    shouldTriggerCelebration
} from './celebrationUtils';
import ScoreSide from './components/ScoreSide';
import SettingsModal from './components/SettingsModal';
import ScoreAnimationToast from './components/ScoreAnimationToast';
import GameNotificationToast from './components/GameNotificationToast';
import CelebrationOverlay from './components/CelebrationOverlay';
import { publishScoreChanged } from './scoreEvents';

const MOTION_PERMISSION_STORAGE_KEY = 'AuroraVolleyQuest.MotionPermission';
const MOTION_PERMISSION_GRANTED = 'granted';

function App() {
    const [settings, setSettings] = useState(loadSettings);
    const [redScore, setRedScore] = useState(0);
    const [blueScore, setBlueScore] = useState(0);
    const [showSettings, setShowSettings] = useState(false);
    const [activeCelebration, setActiveCelebration] = useState(null);
    const previousScoreRef = useRef({ redScore, blueScore });
    const activeCelebrationRef = useRef(null);
    const lastCelebrationAtRef = useRef(null);
    const shakeDetectorRef = useRef(createShakeDetector());

    useEffect(() => {
        saveSettings(settings);
    }, [settings]);

    useEffect(() => {
        const previousScore = previousScoreRef.current;
        if (previousScore.redScore === redScore && previousScore.blueScore === blueScore) {
            return;
        }

        publishScoreChanged({ redScore, blueScore });
        previousScoreRef.current = { redScore, blueScore };
    }, [redScore, blueScore]);

    const winner = getWinner(redScore, blueScore, settings);
    const isGameOver = winner !== null;

    const closeCelebration = useCallback(() => {
        activeCelebrationRef.current = null;
        setActiveCelebration(null);
    }, []);

    const startCelebration = useCallback((now = Date.now()) => {
        if (!settings.enableShakeCelebrations) {
            return false;
        }

        if (!shouldTriggerCelebration({
            isActive: Boolean(activeCelebrationRef.current),
            lastTriggeredAt: lastCelebrationAtRef.current,
            now,
            cooldownMs: CELEBRATION_COOLDOWN_MS
        })) {
            return false;
        }

        const celebration = createCelebration();
        activeCelebrationRef.current = celebration;
        lastCelebrationAtRef.current = now;
        setActiveCelebration(celebration);

        return true;
    }, [settings.enableShakeCelebrations]);

    useEffect(() => {
        if (!settings.enableShakeCelebrations) {
            return undefined;
        }

        const handleManualCelebration = () => {
            startCelebration();
        };

        window.addEventListener('scorekeeper:celebrate', handleManualCelebration);
        return () => window.removeEventListener('scorekeeper:celebrate', handleManualCelebration);
    }, [settings.enableShakeCelebrations, startCelebration]);

    useEffect(() => {
        if (!settings.enableShakeCelebrations) {
            return undefined;
        }

        const DeviceMotion = window.DeviceMotionEvent;
        if (typeof DeviceMotion === 'undefined') {
            return undefined;
        }

        let isMounted = true;
        let listening = false;
        let permissionRequested = false;
        const hasMotionPermission = localStorage.getItem(MOTION_PERMISSION_STORAGE_KEY) === MOTION_PERMISSION_GRANTED;

        shakeDetectorRef.current.reset();

        const handleMotion = (event) => {
            if (shakeDetectorRef.current.isShake(event)) {
                startCelebration();
            }
        };

        const addMotionListener = () => {
            if (!isMounted || listening) {
                return;
            }
            window.addEventListener('devicemotion', handleMotion);
            listening = true;
        };

        const requestPermission = () => {
            if (permissionRequested) {
                return;
            }

            permissionRequested = true;
            DeviceMotion.requestPermission.call(DeviceMotion)
                .then((permissionState) => {
                    if (permissionState === 'granted') {
                        localStorage.setItem(MOTION_PERMISSION_STORAGE_KEY, MOTION_PERMISSION_GRANTED);
                        addMotionListener();
                    }
                })
                .catch(() => {
                    permissionRequested = false;
                });
        };

        if (typeof DeviceMotion.requestPermission === 'function') {
            if (hasMotionPermission) {
                addMotionListener();
            } else {
                window.addEventListener('pointerdown', requestPermission, { once: true, passive: true });
                window.addEventListener('touchstart', requestPermission, { once: true, passive: true });
                window.addEventListener('touchend', requestPermission, { once: true, passive: true });
                window.addEventListener('click', requestPermission, { once: true });
            }
        } else {
            addMotionListener();
        }

        return () => {
            isMounted = false;
            window.removeEventListener('pointerdown', requestPermission);
            window.removeEventListener('touchstart', requestPermission);
            window.removeEventListener('touchend', requestPermission);
            window.removeEventListener('click', requestPermission);
            if (listening) {
                window.removeEventListener('devicemotion', handleMotion);
            }
        };
    }, [settings.enableShakeCelebrations, startCelebration]);

    function increment(team) {
        if (isGameOver) return;
        if (team === Team.RED) {
            setRedScore((prev) => Math.min(prev + 1, settings.maxScore));
        } else {
            setBlueScore((prev) => Math.min(prev + 1, settings.maxScore));
        }
    }

    function decrement(team) {
        if (team === Team.RED) {
            setRedScore((prev) => Math.max(prev - 1, 0));
        } else {
            setBlueScore((prev) => Math.max(prev - 1, 0));
        }
    }

    function clearScores() {
        setRedScore(0);
        setBlueScore(0);
    }

    function handleGameAction() {
        if (isGameOver) {
            clearScores();
            return;
        }

        if (window.confirm('Reset the score?')) {
            clearScores();
        }
    }

    function handleSettingsChange(newSettings) {
        setSettings(clampSettings(newSettings));
    }

    const winnerText = winner
        ? `${winner === Team.RED ? settings.redTeamName : settings.blueTeamName} wins!`
        : null;

    return (
        <div className="app">
            <header className="header" aria-hidden={activeCelebration ? 'true' : undefined}>
                <button
                    className={`header-button ${isGameOver ? 'new-set-button' : 'reset-button'}`}
                    onClick={handleGameAction}
                >
                    {isGameOver ? 'New Set' : 'Reset'}
                </button>
                <h1>Volley Score</h1>
                <div className="header-actions">
                    {settings.enableShakeCelebrations && (
                        <button
                            className="header-button celebrate-button"
                            onClick={() => startCelebration()}
                        >
                            Celebrate
                        </button>
                    )}
                    <button
                        className="header-button settings-button"
                        onClick={() => setShowSettings(true)}
                    >
                        Settings
                    </button>
                </div>
            </header>
            <div className="game" aria-hidden={activeCelebration ? 'true' : undefined}>
                <ScoreSide
                    teamName={settings.redTeamName}
                    score={redScore}
                    isWinner={winner === Team.RED}
                    onIncrement={() => increment(Team.RED)}
                    onDecrement={() => decrement(Team.RED)}
                    colorClass="red"
                />
                <ScoreSide
                    teamName={settings.blueTeamName}
                    score={blueScore}
                    isWinner={winner === Team.BLUE}
                    onIncrement={() => increment(Team.BLUE)}
                    onDecrement={() => decrement(Team.BLUE)}
                    colorClass="blue"
                />
            </div>
            {winnerText && (
                <div className="winner-overlay">
                    <div className="winner-message">
                        <h2>{winnerText}</h2>
                        <p>Game over. Start a new set when you're ready.</p>
                    </div>
                </div>
            )}
            <ScoreAnimationToast
                enabled={settings.showScoreAnimations}
                settings={settings}
            />
            <GameNotificationToast
                enabled={settings.showGameNotifications}
                settings={settings}
            />
            {activeCelebration && (
                <CelebrationOverlay
                    celebration={activeCelebration}
                    onClose={closeCelebration}
                />
            )}
            {showSettings && (
                <SettingsModal
                    settings={settings}
                    onChange={handleSettingsChange}
                    onClose={() => setShowSettings(false)}
                />
            )}
        </div>
    );
}

export default App;
